import { useEffect, useRef } from "react";
import Box from "@mui/material/Box";
import { cleanProps } from "../internal/cleanProps";
import { tokens } from "../tokens";
import { Typography } from "./Typography";
import type { PortalPanelBodyProps, PortalPanelTone } from "../types";

/** CONVENTIONS.md "Alert / help panel styling": help `#f4f7f9`/brand-500
 * (this component's own pre-existing colours), error
 * `status.error.surface`/`.text`, success `status.success.surface`/`.text`.
 * H2 has no "warning" tone — see `PortalPanelTone`'s own doc comment. */
const TONE_SURFACE: Record<PortalPanelTone, { readonly background: string; readonly rule: string }> = {
  help: { background: tokens.colour.neutral.surface, rule: tokens.colour.brand[500] },
  error: { background: tokens.colour.status.error.surface, rule: tokens.colour.status.error.text },
  success: { background: tokens.colour.status.success.surface, rule: tokens.colour.status.success.text },
};

/**
 * Containment note (types.ts): no upstream generic is involved — a plain
 * `Box`.
 *
 * `PortalPanel`'s body on its own (REQ-006), for a trigger that sits
 * somewhere its content may not — inside a `<legend>`, whose content is
 * phrasing-only and becomes the group's accessible name. `PortalPanel`
 * renders this same component inline when given `children`, so the two
 * placements never drift in look or in the `${id}-panel` id the trigger's
 * `aria-controls` names.
 *
 * m1-portal-ux-fixes (Fix A/B): see `PortalPanelBodyProps`'s own doc
 * comment for `tone`/`title` and the `tone="error"` focus-on-mount
 * behaviour this adds.
 */
export function PortalPanelBody({ id, expanded, tone = "help", title, children, testId, spaced, paragraphs }: Readonly<PortalPanelBodyProps>) {
  const panelRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (tone !== "error") {
      return undefined;
    }
    const frame = requestAnimationFrame(() => {
      panelRef.current?.focus();
    });
    return () => cancelAnimationFrame(frame);
    // Mount-only, deliberately with no content key (contrast ErrorSummary's
    // own `itemsKey`-keyed effect): every caller of this tone unmounts the
    // panel (outcome state back to null) before a new outcome can appear,
    // so a fresh mount of THIS component already means "a new outcome just
    // appeared" — see this prop's own doc comment in types.ts.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (!expanded) {
    return null;
  }

  const { background, rule } = TONE_SURFACE[tone];

  return (
    <Box
      id={`${id}-panel`}
      ref={panelRef}
      {...cleanProps({
        "data-testid": testId,
        tabIndex: tone === "error" ? -1 : undefined,
        role: tone === "success" ? "status" : undefined,
      })}
      // ADR-20261102-h2fd: H2's PortalPanel -- 15px ink text at 1.5, 8px by 12px inside a 4px rule.
      sx={{
        display: "block",
        // H2's legendNode panel: normal weight inside a bold legend.
        fontWeight: 400,
        marginTop: spaced === true ? `${tokens.spacing.xs}px` : 0,
        padding: `${tokens.spacing.xs}px ${tokens.spacing.sm}px`,
        backgroundColor: background,
        borderLeft: `4px solid ${rule}`,
        borderRadius: 0,
        fontSize: 15,
        lineHeight: 1.5,
        color: tokens.colour.neutral.ink,
        "& .MuiTypography-body1, & .MuiTypography-body2": { fontSize: "inherit", lineHeight: "inherit" },
      }}
    >
      {title !== undefined ? (
        <Typography variant="heading3" as="p">
          {title}
        </Typography>
      ) : null}
      {children}
      {paragraphs?.map((paragraph) => (
        <Box component="p" key={paragraph} sx={{ margin: "0 0 8px" }}>
          {paragraph}
        </Box>
      ))}
    </Box>
  );
}
